'use client'

/**
 * Sarah's launcher clips, laid over the still face. About 1.5s after the page
 * goes idle she smiles, waves and makes the call-me sign (the gesture clip),
 * then crossfades into the idle loop (smile and blink). The gesture replays
 * about 20s after each play, up to 6 plays per session. The still (avatar.jpg) stays the
 * poster and the fallback: each clip sits over it at opacity 0 until its first
 * frame plays, so there is no poster fetch and no layout shift, and neither can
 * be the LCP element: nothing is fetched until the page has loaded and gone
 * idle. Off under reduced motion, Save-Data, a failed load, or
 * SARAH_AVATAR_VIDEO_ENABLED = false. Paused while the panel is open or the
 * tab is hidden.
 */
import { useEffect, useReducer, useRef, useState } from 'react'
import {
  SARAH_AVATAR_GESTURE_MP4,
  SARAH_AVATAR_GESTURE_WEBM,
  SARAH_AVATAR_VIDEO_ENABLED,
  SARAH_AVATAR_VIDEO_MP4,
  SARAH_AVATAR_VIDEO_WEBM,
  SARAH_CLIPS_AVAILABLE,
  sarahClipSize,
  sarahClipSources,
} from '@/lib/sarah/assets'
import {
  initialSarahClipState,
  SARAH_POINT_IDLE_MS,
  sarahClipLoops,
  sarahClipReducer,
} from '@/lib/sarah/clip-machine'
import {
  readSarahAvatarVideoEnv,
  readSarahGesturePlays,
  recordSarahGesturePlay,
  sarahAvatarVideoAllowed,
  sarahGestureDelay,
} from '@/lib/sarah/avatar-video'

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
  cancelIdleCallback?: (id: number) => void
}

/** Run once the page has loaded and the main thread is idle. */
function afterLoadIdle(cb: () => void): () => void {
  const w = window as IdleWindow
  let idleId = 0
  let timerId = 0
  const schedule = () => {
    if (w.requestIdleCallback) idleId = w.requestIdleCallback(cb, { timeout: 4000 })
    else timerId = window.setTimeout(cb, 1500)
  }
  if (document.readyState === 'complete') schedule()
  else window.addEventListener('load', schedule, { once: true })
  return () => {
    window.removeEventListener('load', schedule)
    if (idleId && w.cancelIdleCallback) w.cancelIdleCallback(idleId)
    if (timerId) window.clearTimeout(timerId)
  }
}

/** wait: still showing, first gesture pending. gesture: gesture on top. loop: idle loop. */
type Phase = 'wait' | 'gesture' | 'loop'

export type SarahChatSignals = {
  /** Sarah is thinking or waiting on a reply. */
  thinking?: boolean
  /** A typed reply is pending (answerSarahChat in flight). */
  pending?: boolean
  /** The visitor is typing in the chat box. */
  typing?: boolean
  /** Count of Sarah answers; a rise plays the thumbs up once. */
  answers?: number
}

/**
 * Event clips once any slot is listed in SARAH_CLIPS_AVAILABLE; until then the
 * shipped idle + gesture behavior runs unchanged.
 */
export function SarahLauncherVideo(
  props: { paused: boolean; enabled?: boolean; clipsEnabled?: boolean } & SarahChatSignals,
) {
  const { clipsEnabled = SARAH_CLIPS_AVAILABLE.size > 0, ...rest } = props
  return clipsEnabled ? <SarahLauncherClips {...rest} /> : <SarahLauncherLegacy paused={rest.paused} enabled={rest.enabled} />
}

const CLIP_WAVED_KEY = 'sarah:clip-waved'
const CLIP_POINTS_KEY = 'sarah:clip-points'
function readSession(key: string): number {
  try {
    return Number(window.sessionStorage.getItem(key)) || 0
  } catch {
    return 0
  }
}
function writeSession(key: string, n: number) {
  try {
    window.sessionStorage.setItem(key, String(n))
  } catch {
    // Per page view only.
  }
}

export function SarahLauncherClips({
  paused,
  enabled = SARAH_AVATAR_VIDEO_ENABLED,
  thinking = false,
  pending = false,
  typing = false,
  answers = 0,
}: { paused: boolean; enabled?: boolean } & SarahChatSignals) {
  const [mount, setMount] = useState(false)
  const [failed, setFailed] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [size, setSize] = useState<240 | 720>(240)
  const [state, dispatch] = useReducer(sarahClipReducer, undefined, () => initialSarahClipState())
  const ref = useRef<HTMLVideoElement | null>(null)
  const lastAnswers = useRef(answers)

  useEffect(() => {
    if (!sarahAvatarVideoAllowed({ enabled, failed: false, ...readSarahAvatarVideoEnv() })) return
    return afterLoadIdle(() => {
      setSize(sarahClipSize(window.innerWidth, window.devicePixelRatio || 1))
      // Wave once per session.
      if (readSession(CLIP_WAVED_KEY) === 0) {
        writeSession(CLIP_WAVED_KEY, 1)
        dispatch({ type: 'load' })
      }
      setMount(true)
    })
  }, [enabled])

  useEffect(() => {
    dispatch({ type: 'signals', signals: { thinking, typing, pending } })
  }, [thinking, typing, pending])

  useEffect(() => {
    if (answers > lastAnswers.current) dispatch({ type: 'answer' })
    lastAnswers.current = answers
  }, [answers])

  // Point after a stretch with no clicks, once per session.
  useEffect(() => {
    if (!mount) return
    let id = 0
    const arm = () => {
      window.clearTimeout(id)
      if (readSession(CLIP_POINTS_KEY) >= 1) return
      id = window.setTimeout(() => {
        writeSession(CLIP_POINTS_KEY, readSession(CLIP_POINTS_KEY) + 1)
        dispatch({ type: 'idle' })
      }, SARAH_POINT_IDLE_MS)
    }
    arm()
    document.addEventListener('pointerdown', arm)
    return () => {
      window.clearTimeout(id)
      document.removeEventListener('pointerdown', arm)
    }
  }, [mount])

  useEffect(() => {
    const v = ref.current
    if (!mount || !v) return
    v.muted = true
    const sync = () => {
      // Panel open pauses only the resting loop; event clips still answer the chat.
      const stop = document.visibilityState === 'hidden' || (paused && state.slot === 'resting')
      if (stop) v.pause()
      else void v.play().catch(() => (state.slot === 'resting' ? setFailed(true) : dispatch({ type: 'failed' })))
    }
    sync()
    document.addEventListener('visibilitychange', sync)
    return () => document.removeEventListener('visibilitychange', sync)
  }, [paused, mount, state.slot])

  if (!mount || failed) return null
  const src = sarahClipSources(state.slot, size)
  const onFail = () => (state.slot === 'resting' ? setFailed(true) : dispatch({ type: 'failed' }))
  return (
    <video
      key={`${state.slot}-${size}`}
      ref={ref}
      className="v3-sarah__avatar-video"
      data-sarah="avatar-video"
      data-sarah-slot={state.slot}
      data-sarah-playing={playing ? '1' : '0'}
      aria-hidden="true"
      tabIndex={-1}
      muted
      loop={sarahClipLoops(state.slot)}
      playsInline
      preload="auto"
      disablePictureInPicture
      disableRemotePlayback
      onPlaying={() => setPlaying(true)}
      onEnded={() => dispatch({ type: 'ended' })}
      onError={onFail}
    >
      <source src={src.webm} type="video/webm" />
      <source src={src.mp4} type="video/mp4" onError={onFail} />
    </video>
  )
}

function SarahLauncherLegacy({
  paused,
  enabled = SARAH_AVATAR_VIDEO_ENABLED,
}: {
  paused: boolean
  enabled?: boolean
}) {
  const [mount, setMount] = useState(false)
  const [failed, setFailed] = useState(false)
  const [phase, setPhase] = useState<Phase>('loop')
  // Render the gesture <video> only while this session still has a play left.
  const [gestureLeft, setGestureLeft] = useState(false)
  const [loopPlaying, setLoopPlaying] = useState(false)
  const [gesturePlaying, setGesturePlaying] = useState(false)
  const loopRef = useRef<HTMLVideoElement | null>(null)
  const gestureRef = useRef<HTMLVideoElement | null>(null)

  // Back to the idle loop. The gesture <video> stays only if a replay is left.
  function endGesture() {
    setGesturePlaying(false)
    setGestureLeft(sarahGestureDelay(readSarahGesturePlays()) !== null)
    setPhase('loop')
  }

  // A gesture that cannot load or play drops out for this page view; the loop stays.
  function dropGesture() {
    setGesturePlaying(false)
    setGestureLeft(false)
    setPhase('loop')
  }

  useEffect(() => {
    if (!sarahAvatarVideoAllowed({ enabled, failed: false, ...readSarahAvatarVideoEnv() })) return
    return afterLoadIdle(() => {
      const plays = readSarahGesturePlays()
      setGestureLeft(sarahGestureDelay(plays) !== null)
      setPhase(plays === 0 ? 'wait' : 'loop')
      setMount(true)
    })
  }, [enabled])

  // Schedule the next gesture. Paused (panel open) holds it; it re-arms on close.
  useEffect(() => {
    if (!mount || !gestureLeft || paused || phase === 'gesture') return
    const delay = sarahGestureDelay(readSarahGesturePlays())
    if (delay === null) return
    const id = window.setTimeout(() => {
      recordSarahGesturePlay()
      setPhase('gesture')
    }, delay)
    return () => window.clearTimeout(id)
  }, [mount, gestureLeft, paused, phase])

  useEffect(() => {
    if (!mount) return
    const loop = loopRef.current
    const gesture = gestureRef.current
    // React does not reflect `muted` as an attribute; autoplay needs it set.
    if (loop) loop.muted = true
    if (gesture) gesture.muted = true
    const sync = () => {
      const stop = paused || document.visibilityState === 'hidden'
      if (loop) {
        if (stop || phase === 'wait') loop.pause()
        else void loop.play().catch(() => setFailed(true))
      }
      if (gesture) {
        if (stop || phase !== 'gesture') gesture.pause()
        else void gesture.play().catch(dropGesture)
      }
    }
    sync()
    document.addEventListener('visibilitychange', sync)
    return () => document.removeEventListener('visibilitychange', sync)
  }, [paused, mount, phase])

  if (!mount || failed) return null

  return (
    <>
      <video
        ref={loopRef}
        className="v3-sarah__avatar-video"
        data-sarah="avatar-video"
        data-sarah-playing={loopPlaying ? '1' : '0'}
        aria-hidden="true"
        tabIndex={-1}
        muted
        loop
        playsInline
        preload="auto"
        disablePictureInPicture
        disableRemotePlayback
        onPlaying={() => setLoopPlaying(true)}
        onError={() => setFailed(true)}
      >
        <source src={SARAH_AVATAR_VIDEO_WEBM} type="video/webm" />
        <source
          src={SARAH_AVATAR_VIDEO_MP4}
          type="video/mp4"
          // The last <source> failing is the load failure; the still stays.
          onError={() => setFailed(true)}
        />
      </video>
      {gestureLeft || phase === 'gesture' ? (
        <video
          ref={gestureRef}
          className="v3-sarah__avatar-video"
          data-sarah="avatar-gesture"
          data-sarah-playing={phase === 'gesture' && gesturePlaying ? '1' : '0'}
          aria-hidden="true"
          tabIndex={-1}
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
          disableRemotePlayback
          onPlaying={() => setGesturePlaying(true)}
          onEnded={endGesture}
          onError={dropGesture}
        >
          <source src={SARAH_AVATAR_GESTURE_WEBM} type="video/webm" />
          <source src={SARAH_AVATAR_GESTURE_MP4} type="video/mp4" onError={dropGesture} />
        </video>
      ) : null}
    </>
  )
}
